// The countdown: a ring with big numerals that drop in each second (variant "ring", the default), or a bar (variant
// "bar", for the producer and ops views). Linear for time: the ring unwinds clockwise from twelve o'clock, driven by the
// Web Animations API on two clipped halves (transform only, so it stays smooth while the page is busy) and synced to the
// server clock. In the last five seconds it turns rose and beats like a heart. Reduced motion: the ring steps once a
// second and nothing pulses. onSecond(left) fires whenever the shown second changes (for ticks, heartbeats, haptics).
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { serverNow } from '@/lib/api';
import { canAnimate, useReducedMotion } from '@/motion/reduced';

export const remainingMs = (deadlineAt: number, now: number) => Math.max(0, deadlineAt - now);
export const secondsLabel = (ms: number) => String(Math.ceil(ms / 1000));
/** ms until the shown whole second next changes (the countdown holds full time until startsAt). */
export function nextTickIn(startsAt: number, deadlineAt: number, now: number): number {
  const total = Math.max(0, deadlineAt - startsAt);
  if (now < startsAt) return startsAt - now + (total % 1000 || 1000);
  const r = remainingMs(deadlineAt, now);
  return r <= 0 ? Infinity : (r % 1000 || 1000);
}
/** Ring geometry for elapsed fraction p: the rotation of the right and left halves in degrees. */
export function ringAngles(p: number): { right: number; left: number } {
  const q = Math.min(1, Math.max(0, p));
  return { right: 45 + 360 * Math.min(0.5, q), left: 45 + 360 * Math.max(0, q - 0.5) };
}

const useIso = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export function Countdown({ startsAt, deadlineAt, onSecond, size = 72, variant = 'ring', dangerAt = 5, className = '' }: {
  startsAt: number; deadlineAt: number;
  onSecond?: (left: number) => void;
  size?: number; variant?: 'ring' | 'bar';
  /** Seconds left at which the countdown turns rose and starts to beat. */
  dangerAt?: number;
  className?: string;
}) {
  const total = Math.max(1, deadlineAt - startsAt);
  const at = () => remainingMs(deadlineAt, Math.max(serverNow(), startsAt));
  const [ms, setMs] = useState(at);
  const reduced = useReducedMotion();
  const [wa] = useState(canAnimate);
  const smooth = wa && !reduced;
  const secs = Math.ceil(ms / 1000);
  const danger = secs > 0 && secs <= dangerAt && serverNow() >= startsAt;

  const onSecondRef = useRef(onSecond); onSecondRef.current = onSecond;
  const shown = useRef(secs);
  const anims = useRef<Animation[]>([]);

  // Update on whole-second boundaries only (no per-frame React work).
  useEffect(() => {
    let t = 0;
    const step = () => {
      const now = serverNow();
      const r = remainingMs(deadlineAt, Math.max(now, startsAt));
      setMs(r);
      const s = Math.ceil(r / 1000);
      if (s !== shown.current) { shown.current = s; if (now >= startsAt) onSecondRef.current?.(s); }
      // Keep the compositor ring honest if the server clock estimate moved.
      for (const a of anims.current) { const want = now - startsAt; if (typeof a.currentTime === 'number' && Math.abs(a.currentTime - want) > 120) a.currentTime = want; }
      const next = nextTickIn(startsAt, deadlineAt, now);
      if (Number.isFinite(next)) t = window.setTimeout(step, next + 12);
    };
    shown.current = Math.ceil(at() / 1000);
    step();
    return () => window.clearTimeout(t);
  }, [startsAt, deadlineAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const rightRef = useRef<HTMLDivElement>(null); const leftRef = useRef<HTMLDivElement>(null); const barRef = useRef<HTMLElement>(null);
  useIso(() => {
    if (!smooth) return;
    const opts: KeyframeAnimationOptions = { duration: total, fill: 'both', easing: 'linear' };
    const list: Animation[] = [];
    if (variant === 'ring' && rightRef.current && leftRef.current) {
      list.push(rightRef.current.animate([{ transform: 'rotate(45deg)' }, { transform: 'rotate(225deg)', offset: 0.5 }, { transform: 'rotate(225deg)' }], opts));
      list.push(leftRef.current.animate([{ transform: 'rotate(45deg)' }, { transform: 'rotate(45deg)', offset: 0.5 }, { transform: 'rotate(225deg)' }], opts));
    } else if (variant === 'bar' && barRef.current) {
      list.push(barRef.current.animate([{ transform: 'scaleX(1)' }, { transform: 'scaleX(0)' }], opts));
    }
    const now = serverNow();
    for (const a of list) a.currentTime = now - startsAt;
    anims.current = list;
    return () => { for (const a of list) a.cancel(); anims.current = []; };
  }, [startsAt, deadlineAt, variant, smooth]);

  // Still positions for reduced motion and environments without WAAPI: step once a second.
  const p = 1 - ms / total;
  const still = ringAngles(p);
  const label = `${secs} ${secs === 1 ? 'second' : 'seconds'} left`;

  if (variant === 'bar') {
    return (
      <div className={`flex items-center gap-3 ${className}`} role="timer" aria-live="off" aria-label={label} data-danger={danger || undefined} style={{ ['--cd-c' as string]: danger ? 'var(--wrong)' : 'var(--time)' } as CSSProperties}>
        <div className="rd-cd-bar"><i ref={barRef as React.RefObject<HTMLElement>} style={smooth ? undefined : { transform: `scaleX(${ms / total})` }} /></div>
        <span aria-hidden="true" className={`numeral w-10 text-right text-3xl leading-none ${danger ? 'text-wrong-strong' : 'text-ink'}`}>{secs}</span>
      </div>
    );
  }

  const w = Math.max(4, Math.round(size * 0.1));
  return (
    <div className={`rd-cd ${className}`} role="timer" aria-live="off" aria-label={label} data-danger={danger || undefined}
      style={{ ['--size' as string]: `${size}px`, ['--w' as string]: `${w}px` } as CSSProperties}>
      <div className="rd-cd-ring" aria-hidden="true">
        <div className="rd-cd-track" />
        <div className="rd-cd-half" data-side="r"><div ref={rightRef} className="rd-cd-arc" style={smooth ? undefined : { transform: `rotate(${still.right}deg)` }} /></div>
        <div className="rd-cd-half" data-side="l"><div ref={leftRef} className="rd-cd-arc" style={smooth ? undefined : { transform: `rotate(${still.left}deg)` }} /></div>
      </div>
      <div className="rd-cd-num" aria-hidden="true"><span key={secs} className={smooth ? 'rd-drop' : undefined}>{secs}</span></div>
    </div>
  );
}
